"use client"
import type React from "react"
import { useMemo, useState, useEffect } from "react"
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
  Spinner,
  Row,
  Col,
  Input,
  InputGroup,
  InputGroupText,
} from "reactstrap"
import {
  FaIdBadge,
  FaHistory,
  FaUser,
  FaBuilding,
  FaUsers,
  FaCertificate,
  FaCreditCard,
  FaCheck,
  FaUserPlus,
  FaEnvelope,
  FaSearch,
  FaTimes,
  FaPaperPlane,
} from "react-icons/fa"
import { toast } from "react-toastify"
import Swal from "sweetalert2"
import DataTable from "react-data-table-component"
import ProfessionalBadgeDesigner from "./professional-badge-designer"
import FocalPersonModal from "./focal-person-modal"
import { ExhibitorRelatedService, type BadgeHistoryItem } from "../../../Service/exhibitorsRelatedService"

interface StaffMember {
  id: number
  person_id: number
  person_full_name: string
  person_email: string
  person_phone: string
  person_id_number: string
  role: string
  exhibitor_name: string
  exhibition_title: string
  exhibition_id?: number
  stand_category: string
  profile_photo_url: string | null
  has_profile_photo: boolean
  total_badges_printed: number
  has_original_badge: boolean
  last_badge_type: string | null
  last_printed_at: string | null
  can_print_original: boolean
  status: boolean
  created_at: string
}

interface StaffSummary {
  total_staff: number
  staff_with_badges: number
  original_badges: number
  copy_badges: number
  total_badges: number
  filtered_count: number
}

interface ExhibitionPricing {
  badge_fee: number
  badge_reprint_price: number
  visitor_entry_fee: number
  parking_fee_per_minute: number
  currency: string
}

interface StaffResponse {
  success: boolean
  staff: StaffMember[]
  summary: StaffSummary
}

interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

interface Props {
  exhibitorId: number
}

// ---- 2026 minimal design tokens ---------------------------------------
// A quiet, confident palette: ink + slate neutrals, one indigo accent,
// and desaturated semantic colors for status. Flat surfaces, hairline
// borders instead of heavy drop shadows, generous radius.
const theme = {
  bg: "#F7F8FA",
  surface: "#FFFFFF",
  border: "#E7E9EE",
  ink: "#12131A",
  slate: "#6B7280",
  slateSoft: "#9CA3AF",
  primary: "#4F46E5",
  primarySoft: "#EEF0FF",
  success: "#0F9D6C",
  successSoft: "#EAFBF3",
  info: "#0284C7",
  infoSoft: "#EFF8FF",
  warning: "#B45309",
  warningSoft: "#FFF7EC",
  danger: "#DC2626",
}

const ProfessionalStaffTab: React.FC<Props> = ({ exhibitorId }) => {
  // Data state
  const [staff, setStaff] = useState<StaffMember[]>([])
  const [summary, setSummary] = useState<StaffSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [totalRows, setTotalRows] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [exhibitionPricing, setExhibitionPricing] = useState<ExhibitionPricing | null>(null)
  const [loadingPricing, setLoadingPricing] = useState(false)
  const [sendingEmail, setSendingEmail] = useState(false)

  // Search
  const [searchTerm, setSearchTerm] = useState("")

  // Modal state
  const [historyModal, setHistoryModal] = useState(false)
  const [badgeDesignerModal, setBadgeDesignerModal] = useState(false)
  const [focalPersonModal, setFocalPersonModal] = useState(false)
  const [focalEmailModal, setFocalEmailModal] = useState(false)
  const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null)
  const [badgeHistory, setBadgeHistory] = useState<BadgeHistoryItem[]>([])
  const [generating, setGenerating] = useState(false)
  const [badgeData, setBadgeData] = useState<any>(null)
  const [templateConfig, setTemplateConfig] = useState<any>(null)

  // Fetch exhibition pricing
  const fetchExhibitionPricing = async (exhibitionId: number) => {
    setLoadingPricing(true)
    try {
      const pricing = await ExhibitorRelatedService.getExhibitionPricing(exhibitionId)
      setExhibitionPricing(pricing)
    } catch (error) {
      console.error("Error fetching exhibition pricing:", error)
      toast.error("Failed to load pricing information")
    } finally {
      setLoadingPricing(false)
    }
  }

  // Fetch staff data with pagination
  const fetchStaffData = async (page = 1, size = 10) => {
    setLoading(true)
    try {
      const data = await ExhibitorRelatedService.getRepresentationsByExhibitor(exhibitorId, page, size)
      if ("staff" in data && "summary" in data) {
        const staffResponse = data as unknown as StaffResponse
        setStaff(staffResponse.staff)
        setSummary(staffResponse.summary)
        setTotalRows(staffResponse.summary.filtered_count)
        if (staffResponse.staff.length > 0 && staffResponse.staff[0].exhibition_id) {
          fetchExhibitionPricing(staffResponse.staff[0].exhibition_id)
        }
      } else if ("results" in data && "count" in data) {
        const paginatedResponse = data as PaginatedResponse<StaffMember>
        setStaff(paginatedResponse.results)
        setTotalRows(paginatedResponse.count)
        if (paginatedResponse.results.length > 0 && paginatedResponse.results[0].exhibition_id) {
          fetchExhibitionPricing(paginatedResponse.results[0].exhibition_id)
        }
      }
    } catch (error) {
      console.error("Error fetching staff data:", error)
      toast.error("Failed to load staff data")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStaffData(currentPage, perPage)
  }, [exhibitorId, currentPage, perPage])

  useEffect(() => {
    if (staff.length > 0 && staff[0].exhibition_id) {
      fetchExhibitionPricing(staff[0].exhibition_id)
    }
  }, [staff])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerRowsChange = (newPerPage: number) => {
    setPerPage(newPerPage)
    setCurrentPage(1)
  }

  const fetchBadgeHistory = async (personId: number) => {
    try {
      const data = await ExhibitorRelatedService.getBadgeHistory(personId)
      if (data.success) {
        setBadgeHistory(data.badge_history)
      } else {
        toast.error("Failed to load badge history")
      }
    } catch (error) {
      console.error("Error fetching badge history:", error)
      toast.error("Failed to load badge history")
    }
  }

  // Handle focal person creation success
  const handleFocalPersonSuccess = () => {
    // Refresh staff data after successful creation
    fetchStaffData(currentPage, perPage)
  }

  // Open the minimal focal-person-email confirmation modal
  const handleSendFocalPersonEmail = () => {
    setFocalEmailModal(true)
  }

  // Actually dispatch the focal person identification email
  const confirmSendFocalPersonEmail = async () => {
    setSendingEmail(true)
    try {
      await ExhibitorRelatedService.sendFocalPersonIdentificationEmail(exhibitorId)
      toast.success("Focal person identification email sent")
      setFocalEmailModal(false)
    } catch (error) {
      console.error("Error sending focal person email:", error)
      toast.error("Failed to send focal person identification email")
    } finally {
      setSendingEmail(false)
    }
  }

  // Rest of the component methods remain the same...
  const confirmReprintPayment = async (
    staff: StaffMember,
    printType: "original" | "copy",
  ): Promise<{ confirmed: boolean; fee: number }> => {
    const reprintFee = exhibitionPricing?.badge_reprint_price || 0
    const currency = exhibitionPricing?.currency || "RWF"
    const result = await Swal.fire({
      title: "Badge Reprint Fee Confirmation",
      html: `
        <div class="text-start p-3">
          <div class="alert alert-warning">
            <h5 class="alert-heading">
              <i class="fas fa-credit-card me-2"></i>Payment Required
            </h5>
            <p class="mb-0">This is a badge reprint and requires payment.</p>
          </div>
          
          <div class="card border-info mb-3">
            <div class="card-body">
              <h6 class="card-title text-info">Staff Details</h6>
              <p><strong>Name:</strong> ${staff.person_full_name}</p>
              <p><strong>Role:</strong> ${staff.role}</p>
              <p><strong>Company:</strong> ${staff.exhibitor_name}</p>
              <p><strong>Print Type:</strong> 
                <span class="badge bg-${printType === "original" ? "warning" : "info"}">
                  ${printType.toUpperCase()} ${printType === "original" ? "(FORCED)" : "(REPRINT)"}
                </span>
              </p>
            </div>
          </div>
          
          <div class="card border-success">
            <div class="card-body">
              <h6 class="card-title text-success">Payment Information</h6>
              <p><strong>Reprint Fee:</strong> 
                <span class="text-success fw-bold fs-4">${reprintFee} ${currency}</span>
              </p>
              <div class="alert alert-info">
                <small>
                  <i class="fas fa-info-circle me-1"></i>
                  Please confirm that the payment has been made through your external banking system.
                </small>
              </div>
            </div>
          </div>
          
          <div class="alert alert-warning">
            <h6 class="mb-2">
              <i class="fas fa-exclamation-triangle me-2"></i>Payment Confirmation
            </h6>
            <p class="mb-0">
              By clicking "Confirm Payment", you are confirming that the badge reprint fee of 
              <strong>${reprintFee} ${currency}</strong> has been paid through external banking.
            </p>
          </div>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `<i class="fas fa-check me-2"></i>Confirm Payment Made`,
      cancelButtonText: `<i class="fas fa-times me-2"></i>Cancel`,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      customClass: {
        popup: "swal-wide",
      },
      focusConfirm: false,
    })

    if (result.isConfirmed) {
      toast.success(`Payment of ${reprintFee} ${currency} confirmed for badge reprint`)
      return { confirmed: true, fee: reprintFee }
    }
    return { confirmed: false, fee: 0 }
  }

  const handleViewHistory = async (staff: StaffMember) => {
    setSelectedStaff(staff)
    await fetchBadgeHistory(staff.person_id)
    setHistoryModal(true)
  }

  const handleBadgeComplete = async () => {
    try {
      // Refresh staff data after badge completion
      // await fetchStaffData(currentPage, perPage)
    } catch (error) {
      console.error("Error completing badge generation:", error)
    }
  }

  const getBadgeStatusColor = (staff: StaffMember): { bg: string; fg: string } => {
    if (staff.total_badges_printed === 0) return { bg: "#F1F2F4", fg: theme.slate }
    if (staff.has_original_badge && staff.last_badge_type === "original")
      return { bg: theme.successSoft, fg: theme.success }
    if (staff.last_badge_type === "copy") return { bg: theme.infoSoft, fg: theme.info }
    return { bg: theme.primarySoft, fg: theme.primary }
  }

  const getBadgeStatusText = (staff: StaffMember) => {
    if (staff.total_badges_printed === 0) return "No badge"
    const type = staff.last_badge_type?.toUpperCase() || "UNKNOWN"
    return `${type} · ${staff.total_badges_printed}`
  }

  // ---- Search filtering (client-side over the current page) -----------
  // Note: since pagination is server-driven, this filters what's already
  // loaded. Wire searchTerm into getRepresentationsByExhibitor as a
  // query param on the backend for full-dataset search.
  const filteredStaff = useMemo(() => {
    const q = searchTerm.trim().toLowerCase()
    if (!q) return staff
    return staff.filter((s) => {
      return (
        s.person_full_name.toLowerCase().includes(q) ||
        s.person_email.toLowerCase().includes(q) ||
        (s.person_phone || "").toLowerCase().includes(q) ||
        (s.person_id_number || "").toLowerCase().includes(q) ||
        s.role.toLowerCase().includes(q) ||
        s.exhibitor_name.toLowerCase().includes(q) ||
        s.exhibition_title.toLowerCase().includes(q)
      )
    })
  }, [staff, searchTerm])

  // Enhanced DataTable columns
  const columns = [
    {
      name: "Photo",
      cell: (row: StaffMember) => (
        <div className="position-relative">
          {row.has_profile_photo ? (
            <img
              src={row.profile_photo_url || "/placeholder.svg"}
              alt={row.person_full_name}
              className="rounded-circle"
              style={{
                width: "44px",
                height: "44px",
                objectFit: "cover",
                border: `1px solid ${theme.border}`,
              }}
            />
          ) : (
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: "44px",
                height: "44px",
                backgroundColor: theme.primarySoft,
                border: `1px solid ${theme.border}`,
              }}
            >
              <FaUser style={{ color: theme.primary }} size={16} />
            </div>
          )}
          {row.status && (
            <div
              className="position-absolute rounded-circle"
              style={{
                width: "10px",
                height: "10px",
                bottom: "0px",
                right: "0px",
                backgroundColor: theme.success,
                border: "2px solid white",
              }}
            />
          )}
        </div>
      ),
      width: "72px",
    },
    {
      name: "Staff Member",
      selector: (row: StaffMember) => row.person_full_name,
      sortable: true,
      cell: (row: StaffMember) => (
        <div className="py-2">
          <div className="fw-semibold" style={{ color: theme.ink, fontSize: "0.925rem" }}>
            {row.person_full_name}
          </div>
          <div className="d-flex flex-column gap-0" style={{ marginTop: "2px" }}>
            <small style={{ color: theme.slate }}>{row.person_email}</small>
            {row.person_phone && <small style={{ color: theme.slateSoft }}>{row.person_phone}</small>}
          </div>
        </div>
      ),
      width: "260px",
    },
    {
      name: "Role & Company",
      selector: (row: StaffMember) => row.role,
      sortable: true,
      cell: (row: StaffMember) => (
        <div>
          <div className="fw-medium" style={{ color: theme.ink, fontSize: "0.875rem" }}>
            {row.role}
          </div>
          <div className="d-flex align-items-center gap-1 mt-1" style={{ color: theme.slate, fontSize: "0.8rem" }}>
            <FaBuilding size={11} />
            <span>{row.exhibitor_name}</span>
          </div>
        </div>
      ),
      width: "200px",
    },
    {
      name: "Exhibition",
      selector: (row: StaffMember) => row.exhibition_title,
      sortable: true,
      cell: (row: StaffMember) => (
        <div>
          <div className="fw-medium" style={{ color: theme.ink, fontSize: "0.85rem" }}>
            {row.exhibition_title}
          </div>
          <small style={{ color: theme.slate }}>{row.stand_category}</small>
        </div>
      ),
      width: "200px",
    },
    {
      name: "Badge Status",
      sortable: true,
      cell: (row: StaffMember) => {
        const status = getBadgeStatusColor(row)
        return (
          <div>
            <span
              className="d-inline-flex align-items-center px-2 py-1 rounded-pill fw-medium"
              style={{ backgroundColor: status.bg, color: status.fg, fontSize: "0.75rem" }}
            >
              {getBadgeStatusText(row)}
            </span>
            {row.can_print_original && (
              <div
                className="d-inline-flex align-items-center gap-1 mt-1 px-2 py-1 rounded-pill fw-medium"
                style={{ backgroundColor: theme.successSoft, color: theme.success, fontSize: "0.7rem" }}
              >
                <FaCheck size={9} />
                Can print original
              </div>
            )}
            {row.last_printed_at && (
              <div className="mt-1" style={{ color: theme.slateSoft, fontSize: "0.75rem" }}>
                Last: {new Date(row.last_printed_at).toLocaleDateString()}
              </div>
            )}
          </div>
        )
      },
      width: "190px",
    },
    {
      name: "",
      cell: (row: StaffMember) => (
        <div className="d-flex gap-1">
          {row.total_badges_printed > 0 && (
            <Button
              size="sm"
              onClick={() => handleViewHistory(row)}
              title="View badge history"
              style={{
                backgroundColor: theme.primarySoft,
                color: theme.primary,
                border: "none",
                borderRadius: "8px",
                width: "34px",
                height: "34px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaHistory size={13} />
            </Button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "90px",
    },
  ]

  const summaryCards = summary
    ? [
        {
          label: "Total Staff",
          value: summary.total_staff,
          icon: FaUsers,
          fg: theme.primary,
          bg: theme.primarySoft,
        },
        {
          label: "Original Badges",
          value: summary.original_badges,
          icon: FaCertificate,
          fg: theme.success,
          bg: theme.successSoft,
        },
        {
          label: "Copy Badges",
          value: summary.copy_badges,
          icon: FaIdBadge,
          fg: theme.info,
          bg: theme.infoSoft,
        },
      ]
    : []

  return (
    <div className="professional-staff-tab" style={{ backgroundColor: theme.bg, padding: "4px" }}>
      {/* Summary cards */}
      {summary && (
        <Row className="mb-4 g-3">
          {summaryCards.map((c) => (
            <Col md="3" key={c.label}>
              <Card
                className="h-100"
                style={{ border: `1px solid ${theme.border}`, borderRadius: "16px", boxShadow: "none" }}
              >
                <CardBody className="p-4">
                  <div
                    className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: "44px", height: "44px", backgroundColor: c.bg }}
                  >
                    <c.icon style={{ color: c.fg }} size={20} />
                  </div>
                  <h3 className="fw-bold mb-0" style={{ color: theme.ink, letterSpacing: "-0.02em" }}>
                    {c.value}
                  </h3>
                  <p className="mb-0 mt-1" style={{ color: theme.slate, fontSize: "0.85rem" }}>
                    {c.label}
                  </p>
                </CardBody>
              </Card>
            </Col>
          ))}
          <Col md="3">
            <Card
              className="h-100"
              style={{ border: `1px solid ${theme.border}`, borderRadius: "16px", boxShadow: "none" }}
            >
              <CardBody className="p-4">
                <div
                  className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                  style={{ width: "44px", height: "44px", backgroundColor: theme.warningSoft }}
                >
                  <FaCreditCard style={{ color: theme.warning }} size={20} />
                </div>
                <h3 className="fw-bold mb-0" style={{ color: theme.ink, letterSpacing: "-0.02em" }}>
                  {loadingPricing ? (
                    <Spinner size="sm" style={{ color: theme.warning }} />
                  ) : (
                    `${exhibitionPricing?.badge_reprint_price || 0} ${exhibitionPricing?.currency || "RWF"}`
                  )}
                </h3>
                <p className="mb-0 mt-1" style={{ color: theme.slate, fontSize: "0.85rem" }}>
                  Reprint Fee
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Staff table */}
      <Card style={{ border: `1px solid ${theme.border}`, borderRadius: "16px", boxShadow: "none" }}>
        <CardHeader
          style={{ backgroundColor: theme.surface, borderBottom: `1px solid ${theme.border}`, padding: "20px 24px" }}
        >
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h5 className="mb-0 fw-bold" style={{ color: theme.ink }}>
                Staff & Badge Management
              </h5>
              <p className="mb-0 mt-1" style={{ color: theme.slate, fontSize: "0.825rem" }}>
                {totalRows} staff member{totalRows === 1 ? "" : "s"} registered
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Button
                size="sm"
                onClick={handleSendFocalPersonEmail}
                disabled={sendingEmail}
                style={{
                  backgroundColor: theme.surface,
                  color: theme.ink,
                  border: `1px solid ${theme.border}`,
                  borderRadius: "10px",
                  padding: "8px 14px",
                  fontWeight: 500,
                  fontSize: "0.85rem",
                }}
              >
                <FaEnvelope className="me-2" style={{ color: theme.slate }} size={13} />
                Send Focal Person Email
              </Button>
              <Button
                size="sm"
                onClick={() => setFocalPersonModal(true)}
                style={{
                  backgroundColor: theme.primary,
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "8px 14px",
                  fontWeight: 500,
                  fontSize: "0.85rem",
                }}
              >
                <FaUserPlus className="me-2" size={13} />
                Add Focal Person
              </Button>
            </div>
          </div>

          {/* Search bar */}
          <div className="mt-3" style={{ maxWidth: "420px" }}>
            <InputGroup>
              <InputGroupText
                style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}`, borderRight: "none" }}
              >
                <FaSearch size={13} style={{ color: theme.slateSoft }} />
              </InputGroupText>
              <Input
                placeholder="Search by name, email, role, or company"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  border: `1px solid ${theme.border}`,
                  borderLeft: "none",
                  boxShadow: "none",
                  fontSize: "0.875rem",
                }}
              />
              {searchTerm && (
                <InputGroupText
                  role="button"
                  onClick={() => setSearchTerm("")}
                  style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}`, borderLeft: "none" }}
                >
                  <FaTimes size={12} style={{ color: theme.slateSoft }} />
                </InputGroupText>
              )}
            </InputGroup>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          <DataTable
            columns={columns}
            data={filteredStaff}
            progressPending={loading}
            progressComponent={
              <div className="text-center py-5">
                <Spinner style={{ color: theme.primary }} />
                <p className="mt-3" style={{ color: theme.slate }}>
                  Loading staff data...
                </p>
              </div>
            }
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            highlightOnHover
            customStyles={{
              headRow: {
                style: {
                  backgroundColor: theme.bg,
                  borderBottom: `1px solid ${theme.border}`,
                  minHeight: "44px",
                },
              },
              headCells: {
                style: {
                  color: theme.slate,
                  fontWeight: 600,
                  fontSize: "0.72rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                },
              },
              rows: {
                style: {
                  fontSize: "14px",
                  borderBottom: `1px solid ${theme.border}`,
                  "&:hover": {
                    backgroundColor: theme.bg,
                  },
                },
              },
            }}
            noDataComponent={
              <div className="text-center py-5">
                <FaUser size={40} className="mb-3" style={{ color: theme.slateSoft, opacity: 0.6 }} />
                <h6 style={{ color: theme.ink }}>
                  {searchTerm ? "No matches found" : "No staff members found"}
                </h6>
                <p className="small mb-0" style={{ color: theme.slate }}>
                  {searchTerm
                    ? "Try a different name, email, role, or company."
                    : "Add staff members to start generating badges"}
                </p>
              </div>
            }
          />
        </CardBody>
      </Card>

      {/* Focal Person creation modal (existing) */}
      <FocalPersonModal
        isOpen={focalPersonModal}
        onClose={() => setFocalPersonModal(false)}
        onSuccess={handleFocalPersonSuccess}
        exhibitorId={exhibitorId}
      />

      {/* Badge Designer Modal */}
      <ProfessionalBadgeDesigner
        isOpen={badgeDesignerModal}
        onToggle={() => setBadgeDesignerModal(false)}
        badgeData={badgeData}
        templateConfig={templateConfig}
        onComplete={handleBadgeComplete}
      />

      {/* Minimal Send Focal Person Email modal */}
      <Modal
        isOpen={focalEmailModal}
        toggle={() => !sendingEmail && setFocalEmailModal(false)}
        centered
        contentClassName="border-0"
        style={{ maxWidth: "380px" }}
      >
        <ModalBody className="p-4 text-center">
          <div
            className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
            style={{ width: "52px", height: "52px", backgroundColor: theme.primarySoft }}
          >
            <FaEnvelope style={{ color: theme.primary }} size={20} />
          </div>
          <h5 className="fw-bold mb-2" style={{ color: theme.ink }}>
            Send focal person email?
          </h5>
          <p className="mb-4" style={{ color: theme.slate, fontSize: "0.9rem" }}>
            This sends the company a registration link and management guide, asking them to designate a focal
            person for this exhibition.
          </p>
          <div className="d-flex gap-2">
            <Button
              className="flex-grow-1"
              onClick={() => setFocalEmailModal(false)}
              disabled={sendingEmail}
              style={{
                backgroundColor: theme.surface,
                color: theme.ink,
                border: `1px solid ${theme.border}`,
                borderRadius: "10px",
                padding: "10px",
                fontWeight: 500,
              }}
            >
              Cancel
            </Button>
            <Button
              className="flex-grow-1"
              onClick={confirmSendFocalPersonEmail}
              disabled={sendingEmail}
              style={{
                backgroundColor: theme.primary,
                color: "#fff",
                border: "none",
                borderRadius: "10px",
                padding: "10px",
                fontWeight: 500,
              }}
            >
              {sendingEmail ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <FaPaperPlane className="me-2" size={12} />
                  Send
                </>
              )}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* Badge History Modal */}
      <Modal isOpen={historyModal} toggle={() => setHistoryModal(false)} size="xl" contentClassName="border-0">
        <ModalHeader
          toggle={() => setHistoryModal(false)}
          style={{ backgroundColor: theme.surface, borderBottom: `1px solid ${theme.border}` }}
        >
          <span style={{ color: theme.ink, fontWeight: 600 }}>
            <FaHistory className="me-2" style={{ color: theme.primary }} />
            Badge History — {selectedStaff?.person_full_name}
          </span>
        </ModalHeader>
        <ModalBody className="p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr style={{ backgroundColor: theme.bg }}>
                  <th style={{ color: theme.slate, fontSize: "0.75rem", textTransform: "uppercase" }}>Print Type</th>
                  <th style={{ color: theme.slate, fontSize: "0.75rem", textTransform: "uppercase" }}>Sequence</th>
                  <th style={{ color: theme.slate, fontSize: "0.75rem", textTransform: "uppercase" }}>
                    Verification Code
                  </th>
                  <th style={{ color: theme.slate, fontSize: "0.75rem", textTransform: "uppercase" }}>Exhibition</th>
                  <th style={{ color: theme.slate, fontSize: "0.75rem", textTransform: "uppercase" }}>Printed By</th>
                  <th style={{ color: theme.slate, fontSize: "0.75rem", textTransform: "uppercase" }}>Date</th>
                  <th style={{ color: theme.slate, fontSize: "0.75rem", textTransform: "uppercase" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {badgeHistory.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-5">
                      <FaHistory size={40} style={{ color: theme.slateSoft, opacity: 0.6 }} className="mb-3" />
                      <div style={{ color: theme.slate }}>
                        <h6 style={{ color: theme.ink }}>No badge history found</h6>
                        <p className="small mb-0">This staff member hasn't generated any badges yet.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  badgeHistory.map((badge) => (
                    <tr key={badge.id}>
                      <td>
                        <span
                          className="px-2 py-1 rounded-pill fw-medium"
                          style={{
                            fontSize: "0.75rem",
                            backgroundColor: badge.print_type === "original" ? theme.successSoft : theme.infoSoft,
                            color: badge.print_type === "original" ? theme.success : theme.info,
                          }}
                        >
                          {badge.print_type.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <code
                          className="px-2 py-1 rounded"
                          style={{ backgroundColor: theme.bg, color: theme.ink }}
                        >
                          #{badge.print_sequence.toString().padStart(4, "0")}
                        </code>
                      </td>
                      <td>
                        <code
                          className="small px-2 py-1 rounded"
                          style={{ backgroundColor: theme.bg, color: theme.ink }}
                        >
                          {badge.verification_code}
                        </code>
                      </td>
                      <td className="small" style={{ color: theme.ink }}>
                        {badge.exhibition_title}
                      </td>
                      <td className="small" style={{ color: theme.ink }}>
                        {badge.printed_by_name}
                      </td>
                      <td className="small" style={{ color: theme.ink }}>
                        {new Date(badge.printed_at).toLocaleDateString()}
                        <br />
                        <span style={{ color: theme.slate }}>
                          {new Date(badge.printed_at).toLocaleTimeString()}
                        </span>
                      </td>
                      <td>
                        <span
                          className="px-2 py-1 rounded-pill fw-medium"
                          style={{
                            fontSize: "0.75rem",
                            backgroundColor: badge.is_active ? theme.successSoft : "#F1F2F4",
                            color: badge.is_active ? theme.success : theme.slate,
                          }}
                        >
                          {badge.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </ModalBody>
      </Modal>

      <style>{`
        .professional-staff-tab .rdt_Pagination {
          border-top: 1px solid ${theme.border} !important;
        }
      `}</style>
    </div>
  )
}

export default ProfessionalStaffTab